import classNames from 'classnames';
import Text from '@hiredigital/ui/Text/Text';
import Header from '@components/Header/DefaultHeader';
import Layout from '@components/Layout/Layout';
import { Section } from '@components/Section';
import Hero from '@components/PageComponents/Hero/Hero';

import Styles from './Styles.module.scss';

const PrivacyPage = ({ ...props }) => {
  const head = {
    title: `Privacy | Hire Digital`,
    meta: [
      {
        name: 'description',
        content:
          'Hire Digital is committed to protecting and respecting your privacy. This policy, together with our Terms of Use and any other documents referred to in it, sets out the basis on which any personal data we collect from you, or that you provide to us, will be processed by us.',
      },
    ],
  };

  return (
    <Layout header={Header} pageHead={head}>
      <Hero parent={Hero.Page.NEW_HERO}>
        <div className={classNames(Hero.Styles.heroContainer, Styles.hero)}>
          <h1
            className={classNames(
              Hero.Styles.heroTitle,
              Styles.title
            )}>{`Website Privacy Policy`}</h1>
        </div>
      </Hero>

      <div className={Styles.container}>
        <Section className={Section.Styles.medium}>
          <Text type={Text.Type.PARAGRAPH}>
            {`Hire Digital is committed to protecting and respecting your privacy.`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`This policy, together with our Terms of Use (`}
            <a href='https://www.hiredigital.com/terms'>{`www.hiredigital.com/terms`}</a>
            {`)
              and any other documents referred to in it, sets out the basis on
              which any personal data we collect from you, or that you provide
              to us, will be processed by us. Please read the following
              carefully to understand our views and practices regarding your
              personal data and how we will treat it.`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`If you are under 18, please do not send any personal data about
              yourself to us.`}
          </Text>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`1. Introduction`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`Welcome to the Hire Digital website ("Site"). Hire Digital Corporation ("Hire Digital", "we", "us", and/or "our") operates this Site to provide our users ("you" and "your") with information about Hire Digital and its platform.`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`Hire Digital is used by professional freelancers ("Freelancers") who wish to offer their development, engineering and other services ("Services") and clients who need the Services ("Clients"). Freelancers that have passed our screening process and are admitted to our network of professional Freelancers ("Network") are referred to as "Talent". For purposes of this policy the term "Freelancers" is broader in scope and includes both Talent and Freelancers who are not Talent. The Personal Data we receive from Freelancers and Clients and how we handle it differs, so we explain our data practices for Freelancers and Clients in two distinct sections below. Navigate to the section that applies to you: Freelancers; Clients.`}
          </Text>
          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`2. Scope`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`This Privacy Policy sets out how Hire Digital collects, retains, and uses information, including personal data ("Personal Data") about Freelancers, Clients, and other Site visitors and individuals, which include our vendors, partners, blog readers, and job applicants. The Privacy Policy also applies to users of Hire Digital software offerings, and data that Hire Digital collects in-person, for instance, at business conferences and trade shows.`}
          </Text>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`3. Freelancers`}
          </Text>
          <ol className={Styles.listLowerRoman}>
            <li>
              <Text size={Text.Size.HEADING_TWO}>{`Information We Collect`}</Text>
              <ol className={Styles.listLowerAlpha}>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Information That You Provide To Us`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`When you apply as a Freelancer on the Site, you are asking to be admitted to our Network. In order to admit you to the Network and match your qualifications with our Client’s needs, we need to perform a screening process, which evaluates your language skills, personality, domain-specific knowledge, competence and professionalism. We may also need to screen potential Freelancers for specific technical skills, as required by our Clients. In this context, you will be asked to provide information about yourself, which includes:`}
                  </Text>
                  <ul>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`your name`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`contact information`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`address`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`location`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`work experience`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`education`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`photo`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`social network information`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`family information`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`billing information`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`identification`}</Text>
                    </li>
                  </ul>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`Our provider of background check services may collect additional data about you (including information about criminal convictions or offenses) if and when we undertake vetting / background checks. If applicable to you, your consent to the collection of such additional data will be solicited at such time, pursuant to applicable laws.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`When you participate in the community of Hire Digital’s experts on our Site, we will collect the information that you elect to share in that context.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`We will use certain of your Personal Data to enable you to create a profile to be displayed within the Services; this profile is searchable and may be viewed by Clients seeking Freelancers.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`Once you are admitted to the Network and you are matched with a Client, we will receive from our Clients information regarding the performance of your services to that Client, as necessary for maintaining standards of integrity and excellence in our Network.`}
                  </Text>
                </li>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Automatically Collected Data`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`When you interact with Hire Digital through the Site or services, we collect automatically certain information. Please read the "Site Visitors" section below for more information.`}
                  </Text>
                </li>
              </ol>
            </li>

            <li>
              <Text size={Text.Size.HEADING_TWO}>{`How We Use the Information`}</Text>

              <ol className={Styles.listLowerAlpha}>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`To Provide The Service And Respond To Requests`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`Hire Digital uses the Personal Data you provide in a manner that is consistent with this Privacy Policy. If you provide Personal Data for a certain reason, we will use the Personal Data in connection with the reason for which it was provided.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`In particular, we use data that you provide to us (described above) in order to process your request to become a Freelancer and administer our contract with you once you become a member of the Network. For individuals in the EU, our use of Personal Data that you provide to us is necessary in order to implement your request to become a Freelancer prior to entering into a contract with us and, once you are admitted to our Network, to perform our contract with you.`}
                  </Text>
                </li>

                <li>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`As Necessary For Certain Legitimate Interests`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`We use your Personal Data for the legitimate interests described below:`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`To send administrative information to you, for example, information regarding the Site and changes to our terms, conditions, and policies.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`To conduct analytics on how the Site and our Service are being used by you for our internal purposes, namely for providing, maintaining, benchmarking and improving our offerings, and identifying usage trends.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`We use data relating to your use of and interaction with the Site and the Services, including use of product features by you and information that we obtain through cookies and other technologies, to better understand your needs and interests in order to personalize your experience with our Services.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`To market our services and showcase Freelancers (in particular, we can feature certain profiles of Freelancers to drive traffic from potential Clients).`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`To prevent fraud or criminal activity, misuses of our products or services, and ensure the security of our IT systems, architecture and networks.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`To (a) comply with legal obligations and legal process, (b) respond to requests from public and government authorities including public and government authorities outside your country of residence; (c) enforce our terms and conditions; (d) protect our operations; (e) protect our rights, privacy, safety or property, and/or that of you or others; and (f) allow us to pursue available remedies or limit the damages that we may sustain.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`If you ask us to delete your data and we are required to fulfil your request, we may keep basic data to identify you and prevent further unwanted processing.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`For individuals in the EU, please see the "EU Individuals" section below for information on our legitimate interests and your rights.`}
                  </Text>
                </li>
              </ol>
            </li>
          </ol>
          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`4. Clients`}
          </Text>

          <ol className={Styles.listLowerRoman}>
            <li>
              <Text size={Text.Size.HEADING_TWO}>{`Information We Collect`}</Text>
              <ol className={Styles.listLowerAlpha}>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Information That You Provide To Us`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`We collect Personal Data from our prospective Clients when we receive a request for hiring our Freelancers. This includes names and contact information of prospective Client’s representatives, and any information relating to the Client’s needs. We also collect Personal Data that is entered on our Site or sent to us electronically, for example when you complete a web form to give Personal Data to us directly (such as on our "Contact Us" page), when you voluntarily provide such information such as when you register for access to the platform (for example, your name and mailing address), contact us with inquiries or respond to one of our surveys. Wherever Hire Digital collects Personal Data we make an effort to provide a link to this Privacy Policy.`}
                  </Text>
                </li>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`Information Obtained From Third Parties`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`We may supplement the information that we collected from you (such as your email address) with additional information about you and your company obtained from publicly available and third-party databases or services that provide information about business people and companies (including an individual’s name, job title, business contact information, and company information).`}
                  </Text>
                </li>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Automatically Collected Data`}</Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`When you interact with Hire Digital through the Site or services, we collect automatically certain information. Please read the "Site Visitors" section below for more information.`}
                  </Text>
                </li>
              </ol>
            </li>

            <li>
              <Text size={Text.Size.HEADING_TWO}>{`How We Use the Information`}</Text>

              <ol className={Styles.listLowerAlpha}>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`To Provide The Services And Respond To Requests`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`Hire Digital uses the Personal Data you provide in a manner that is consistent with this Privacy Policy. If you provide Personal Data for a certain reason, we may use the Personal Data in connection with the reason for which it was provided. For instance, if you contact us by email, we will use the Personal Data you provide to answer your question or resolve your problem. Also, if you provide Personal Data in order to obtain access to the Services, we will use your Personal Data to provide you with access to such Services, maintain your account, contact you regarding your use of the Services and/or the Site or to notify you of important changes to the Services and/or the Site, and to monitor your use of such services. For individuals in the EU, such use is necessary to respond to or implement your request and for the performance of the contract between you and us.`}
                  </Text>
                </li>

                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`For Marketing Purposes`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`We may use your contact details to tell you about services we believe will be of interest to you, upcoming events or other promotions. If we do so, each marketing communication we send you will contain instructions permitting you to "opt out" of receiving future marketing communications. Note however that as user of our services you cannot opt out of some administrative communications that are reasonably necessary to the services, such as billing or service notifications. In addition, if at any time you wish not to receive any future marketing communications or you wish to have your name deleted from our mailing lists, please contact us as indicated below.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`Where required by applicable law (for example, if you are an individual in the EU), we will only send you marketing information by email if you consent to us doing so at the time you provide us with your Personal Data. When you provide us with your consent to be contacted for marketing purposes, you have the right to withdraw your consent at any time by following the instructions to "opt-out" of receiving marketing communication in each marketing email we send you or by contacting us as indicated below.`}
                  </Text>
                </li>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`As Necessary For Certain Legitimate Interests`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`We use your Personal Data for the legitimate interests described below:`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`To send administrative information to you, for example, information regarding the Site and changes to our terms, conditions, and policies.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`To respond to your inquiries and fulfil your requests, such as to send you requested materials and newsletters, as well as information and materials regarding our products and services.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`To conduct analytics on how the Site and our Service are being used by you for our internal purposes (namely for providing, maintaining, benchmarking and improving our offerings, identifying usage trends and determining the effectiveness of our promotional campaigns) and to inform our marketing strategy and personalize our communications with you (including providing information on our features and other marketing and service-related announcements relevant to the content and features you engage with).`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`To supplement the information that we collected from you with information obtained from third parties (described above) in order to update, expand and analyze our records, identify new Clients, and provide products and services that may be of interest to you.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`To prevent fraud or criminal activity, misuses of our products or services, and ensure the security of our IT systems, architecture and networks.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`To (a) comply with legal obligations and legal process, (b) respond to requests from public and government authorities including public and government authorities outside your country of residence; (c) enforce our terms and conditions; (d) protect our operations; (e) protect our rights, privacy, safety or property, and/or that of you or others; and (f) allow us to pursue available remedies or limit the damages that we may sustain.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`If you ask us to delete your data and we are required to fulfil your request, we may keep basic data to identify you and prevent further unwanted processing.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`For individuals in the EU, please see the "EU Individuals" section below for information on our legitimate interests and your rights.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`If Hire Digital intends to use any Personal Data in any manner that is not consistent with this Privacy Policy, you will be informed of such anticipated use prior to or at the time at which the Personal Data is collected and pursuant to the applicable law.`}
                  </Text>
                </li>
              </ol>
            </li>
          </ol>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`5. Job Applicants`}
          </Text>
          <ol className={Styles.listLowerRoman}>
            <li>
              <Text size={Text.Size.HEADING_TWO}>{`Information We Collect`}</Text>

              <ol className={Styles.listLowerAlpha}>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Application and assessment process`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`When you apply for employment through our Site, our provider of recruiting services will collect the Personal Data that you provide in your resume and cover letter. It is for you to decide what you include in these documents. However, the kinds of information you may wish to include are: your name, contact details, employment history and education.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`If you are invited to undertake further assessments (such as an interview) in connection with your application and you participate in such assessments, we will collect further Personal Data that you provide to us as part of that process. The kinds of information you may disclose include information about your qualifications and information about your employment experience.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`If you do not provide us with certain information when requested, it may impact our ability to assess your suitability for a role with us or we may not be able to make you an offer of employment.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`Throughout the recruitment process, we may create Personal Data in connection with the assessment of your application. For example, we may record the views of those considering your application about your suitability for the role for which you have applied and retain interview notes.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`We have a legitimate interest in facilitating the interview process and communicating offers of employment to you, making informed recruitment decisions and selecting suitable candidates for roles with us, and improving our recruiting and hiring processes.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`We will retain your contact details and resume to inform you of current and future career opportunities (unless you tell us that you do not want us to keep your details for that purpose) pursuant to our legitimate interest in doing so.`}
                  </Text>
                </li>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`If your application is successful`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`If your application is successful we will collect further Personal Data about you in connection with conducting reference and background checks where required or permitted by applicable local law (including if applicable to you special categories of Personal Data, and your consent to the collection of such additional data will be solicited at such time, pursuant to applicable laws). We will also collect copies of relevant identification documents from you (such as your passport or driving license, proof of address, a copy of your work permit (where applicable), a photograph and a copy of your signature). We use this information to comply with immigration requirements and to verify your identity for our own internal security purposes. We have a legitimate interest in collecting and using this data to comply with our legal obligations and for the performance of your employment contract with us.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`If you are hired, your Personal Data will be used as part of your employee record under our employee privacy policies.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`Please see the "EU Individuals" section below for information on your rights in relation to the Personal Data we hold about you.`}
                  </Text>
                </li>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Automatically Collected Data`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`When you interact with Hire Digital through our Site in relation to your application, we collect automatically certain information. Please read the "Site Visitors" section below for more information.`}
                  </Text>
                </li>
              </ol>
            </li>
          </ol>
          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`6. Site Visitors`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`When you interact with Hire Digital through the Site or the Services (whether as a Freelancer, as a Client, a job applicant, a blog reader, software end user, external domain visitor, or other visitor to our Site), we and our service providers acting on our behalf will automatically collect information about you through cookies (small text files placed on your device) and other technologies. Please see our Cookie Policy to learn more about how we use cookies and other technologies.`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`As a visitor to our Site, our servers record information ("log data"), including information that your browser automatically sends whenever you visit the Site. This log data includes your Internet Protocol ("IP") address (from which we understand the country you are connecting from at the time you visit the Site), browser type and settings, the date and time of your request.`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`Where the information that we collect automatically on our Site is Personal Data, our legal basis for the use of this information is that it is necessary for the purposes of our legitimate interests in maintaining the safe operation of our Site as well as in learning how Site visitors interact with our Site to improve your use of it.`}
          </Text>
          <ol className={Styles.listLowerRoman}>
            <li>
              <Text size={Text.Size.HEADING_TWO}>{`External Domain Visitors`}</Text>
              <Text type={Text.Type.PARAGRAPH}>
                {`Hire Digital owns, leases, or operates several sites and services in which this section applies, but their domains are external to hiredigital.com.`}
              </Text>
            </li>
          </ol>
          <br />
          <Text element='h2' size={Text.Size.HEADLINE} style={{ marginBottom: '40px' }}>
            {`Website Cookie Policy`}
          </Text>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`7. External Domain Visitors`}
          </Text>
          <ol className={Styles.listLowerRoman}>
            <li>
              <Text size={Text.Size.HEADING_TWO}>{`Hire Digital`}</Text>
              <Text type={Text.Type.PARAGRAPH}>
                {`Visitors to Hire Digital may opt to provide their name and email. We will use this data to respond to any requests you forward to us or to transmit the requested content to you, such as the Hire Digital newsletter, or to share information and materials regarding Hire Digital products and services. This information is collected on a consent basis and is not required to access the site.`}
              </Text>
              <Text type={Text.Type.PARAGRAPH}>
                {`Hire Digital operates as an extension of Content.co. The data collected as part of the Hire Digital operations are in the same system, and connected to, Content.co operating data. We may directly access, or cross reference, the data for purposes including Analytics, Client Experience, Lead Conversion, Marketing, and Technical Support and Operations.`}
              </Text>
            </li>
          </ol>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`8. Events`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`We may collect Personal Data from you when you attend one of our events, for instance at business conferences and trade shows. The data we collect includes information that you voluntarily give to us, such as your business card or contact details. We will use this information to develop a business relationship and in a manner consistent with the purpose for which you gave the information.`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`Where required by applicable law (for example, if you are an individual in the EU), we will ask your consent before or when sending you marketing information by email. When you provide us with your consent to be contacted for marketing purposes, you have the right to withdraw your consent at any time by following the instructions to "opt-out" of receiving marketing communication in each marketing email we send you or by contacting us as indicated below.`}
          </Text>
          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`9. Our Disclosure of Your Personal Data and Other Information`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`Hire Digital is not in the business of selling your information. We consider this information to be a vital part of our relationship with you. There are, however, certain circumstances in which we may share your Personal Data with certain third parties, as set forth below:`}
          </Text>
          <ol className={Styles.listLowerRoman}>
            <li>
              <Text size={Text.Size.HEADING_TWO}>{`Business Transfers`}</Text>
              <Text type={Text.Type.PARAGRAPH}>
                {`As we develop our business, we might sell or buy businesses or assets. In the event of a corporate sale, merger, reorganization, dissolution or similar event, Personal Data may be part of the transferred assets.`}
              </Text>
            </li>

            <li>
              <Text size={Text.Size.HEADING_TWO}>
                {`Agents, Consultants and Other Service Providers`}
              </Text>
              <Text type={Text.Type.PARAGRAPH}>
                {`Hire Digital, like many businesses, sometimes hires other companies to perform certain business-related functions. These parties include website analytics companies, providers of digital advertising services, our hosting and cloud computing service providers, providers of CRM, marketing and sales software solutions, providers of billing and processing payments functions, providers of background check services. When we employ another company to perform a function of this nature, we only provide them with the information that they need to perform their specific function. Pursuant to our instructions, these parties may access, process or store Personal Data in the course of performing their duties to us and solely in order to perform the services we have hired them to provide.`}
              </Text>
            </li>

            <li>
              <Text size={Text.Size.HEADING_TWO}>{`Legal Requirements`}</Text>
              <Text type={Text.Type.PARAGRAPH}>
                {`Hire Digital may disclose your Personal Data if required to do so by law or in the good faith belief that such action is necessary to (i) comply with a legal obligation, (ii) protect and defend the rights or property of Hire Digital, (iii) act in urgent circumstances to protect the personal safety of users of the Site or the public, or (iv) protect against legal liability.`}
              </Text>
            </li>
          </ol>
          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`10. California Residents`}
          </Text>
          <ol className={Styles.listLowerRoman}>
            <li>
              <Text size={Text.Size.HEADING_TWO}>{`Scope`}</Text>
              <Text type={Text.Type.PARAGRAPH}>
                {`This section of the policy applies solely to California residents, in accordance with the California Consumer Privacy Act of 2018 ("CCPA"). As with other provisions of the Privacy Policy, this section details what Personal Data Hire Digital collects, and how and why we collect this data. It’s important to note that Hire Digital will never sell your data (as such term is defined in the CCPA), however, we may share your data through the course of business. Please see the section above titled, Our Disclosure of Your Personal Data and Other Information.`}
              </Text>

              <ol className={Styles.listLowerAlpha}>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`The data we collect`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`Hire Digital collects Personal Data (from you) as part of the Talent application process, as well as the onboarding process for Clients. We collect information such as the following:`}
                  </Text>

                  <ul>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`your name`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`contact information`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`address`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`location`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`work experience`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`education`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`photo`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`social network information`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`family information`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`billing information`}</Text>
                    </li>
                    <li>
                      <Text type={Text.Type.PARAGRAPH}>{`identification`}</Text>
                    </li>
                  </ul>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`As stated in the sections above, our provider of background check services may request additional information. The data you provide is housed in internal databases for use during the recruiting and job selection process for Clients. A detailed explanation of the types of data we collect and its sources can be found under the above sections Clients, Freelancers, and Job Applicants. Hire Digital also collects technical information through the use of cookies. For more information, please see the Cookie Policy, and the section above titled, Site Visitors.`}
                  </Text>
                </li>

                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Your rights`}</Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`CCPA affords you certain rights with regard to data collection and deletion. As of January 1, 2020, the CCPA gives you the following rights:`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Right of access. `}</strong>
                    {`Ask us what categories and types of data we have collected for you over the last 12 months, why the data was collected, and request a copy of such data.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Right of deletion. `}</strong>
                    {`Request deletion of the data we have for you.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Right to not be discriminated against. `}</strong>
                    {`You cannot be discriminated against for making a data inquiry.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`To contact us, please see the section titled, Contacting Hire Digital. California residents are allowed two inquiries during a 12-month period. We will respond to your inquiry no later than 45 days after receipt.`}
                  </Text>
                </li>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Denial of data deletion requests`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`In accordance with the CCPA, Hire Digital may deny your request to delete data if it meets one of the following criteria:`}
                  </Text>

                  <ul>
                    <li>
                      <Text
                        type={
                          Text.Type.PARAGRAPH
                        }>{`The data is needed to complete a transaction`}</Text>
                    </li>
                    <li>
                      <Text
                        type={
                          Text.Type.PARAGRAPH
                        }>{`We have legal obligations for our company or industry`}</Text>
                    </li>
                    <li>
                      <Text
                        type={
                          Text.Type.PARAGRAPH
                        }>{`The data is needed for security and technical purposes`}</Text>
                    </li>
                    <li>
                      <Text
                        type={
                          Text.Type.PARAGRAPH
                        }>{`In connection with the right to free speech`}</Text>
                    </li>
                    <li>
                      <Text
                        type={
                          Text.Type.PARAGRAPH
                        }>{`Required for research as defined by the Act`}</Text>
                    </li>
                    <li>
                      <Text
                        type={
                          Text.Type.PARAGRAPH
                        }>{`There is a legitimate interest to keep the data. To understand the scope of "legitimate interest," please see the applicable section above titled, How We Use the Information`}</Text>
                    </li>
                  </ul>
                </li>
              </ol>
            </li>
          </ol>
          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`11. EU Individuals`}
          </Text>
          <ol className={Styles.listLowerRoman}>
            <li>
              <Text size={Text.Size.HEADING_TWO}>{`Scope`}</Text>
              <Text type={Text.Type.PARAGRAPH}>
                {`This section applies solely to individuals in the EU (for these purposes, reference to the EU also includes the European Economic Area countries of Iceland, Liechtenstein and Norway and, where applicable, Switzerland). Our Privacy Policy describes why and how Hire Digital collects, uses and stores your Personal Data, the lawful basis on which your Personal Data is processed, and what your rights and our obligations are in relation to such processing (please see "Your Rights" section below).`}
              </Text>

              <ol className={Styles.listLowerAlpha}>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Data Controller`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`Hire Digital is the data controller for processing your Personal Data. The data controller is responsible for deciding how Personal Data about you is used. Please see the "Contacting Hire Digital" section below to find out how to contact us, which also provides the contact details of our representative in the EU for purposes of the General Data Protection Regulation.`}
                  </Text>
                </li>

                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Your Rights`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`Subject to applicable EU law, you have the following rights in relation to your Personal Data:`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Right of access. `}</strong>
                    {`If you ask us, we will confirm whether we are processing your Personal Data and, if so, provide you with a copy of that Personal Data along with certain other details. If you require additional copies, we may need to charge a reasonable fee.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Right to rectification. `}</strong>
                    {`If your Personal Data is inaccurate or incomplete, you are entitled to ask that we correct or complete it. If we shared your Personal Data with others, we will tell them about the correction where possible. If you ask us, and where possible and lawful to do so, we will also tell you with whom we shared your Personal Data so you can contact them directly.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Right to erasure. `}</strong>
                    {`You may ask us to delete or remove your Personal Data. If we shared your data with others, we will tell them about the erasure where possible. If you ask us, and where possible and lawful to do so, we will also tell you with whom we shared your Personal Data with so you can contact them directly.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Right to restrict processing. `}</strong>
                    {`You may ask us to restrict or ‘block’ the processing of your Personal Data in certain circumstances, such as where you contest the accuracy of the data or object to us processing it. We will tell you before we lift any restriction on processing. If we shared your Personal Data with others, we will tell them about the restriction where possible. If you ask us, and where possible and lawful to do so, we will also tell you with whom we shared your Personal Data so you can contact them directly.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Right to data portability. `}</strong>
                    {`You have the right to obtain your Personal Data from us that you consented to give us or that was provided to us as necessary in connection with our contract with you. We will give you your Personal Data in a structured, commonly used and machine-readable format. You may reuse it elsewhere.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Right to object. `}</strong>
                    {`You may ask us at any time to stop processing your Personal Data, and we will do so: (1) If we are relying on a legitimate interest to process your Personal Data – unless we demonstrate compelling legitimate grounds for the processing or (2) If we are processing your Personal Data for direct marketing.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Rights in relation to automated decision-making and profiling. `}</strong>
                    {`You have the right to be free from decisions based solely on automated processing of your Personal Data, including profiling, which produce a significant legal effect on you, unless such profiling is necessary for entering into, or the performance of, a contract between you and us, or with your explicit consent. We are not currently processing your Personal Data for such type of automated decision-making, including profiling, but if we elect to do so in the future we will provide you with notice and choice, in accordance with EU data protection law.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Right to withdraw consent. `}</strong>
                    {`If we rely on your consent to process your Personal Data, you have the right to withdraw that consent at any time, but this will not affect any processing of your data that has already taken place.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    <strong>{`Right to lodge a complaint with the data protection authority. `}</strong>
                    {`If you have a concern about our privacy practices, including the way we handled your Personal Data, you can report it to the data protection authority that is authorized to hear those concerns.`}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`You may exercise your rights by contacting us as indicated under "Contacting Hire Digital" section below.`}
                  </Text>
                </li>

                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Legitimate Interest`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`"Legitimate interests" means our interests in conducting and managing our organization and delivering the best Services to you. This Privacy Policy describes when we process your Personal Data for our legitimate interests, what these interests are and your rights. We will not use your Personal Data for activities where the impact on you overrides our interests, unless we have your consent or those activities are otherwise required or permitted by law. You have the right to object to processing that is based on our legitimate interests. For more information on your rights, please see "Your Rights" section above.`}
                  </Text>
                </li>
                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Data Transfers`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`Hire Digital is based in the United States. When you apply as a Freelancer or use our services as a Client, 
                      or otherwise use our Site, your Personal Data will be transmitted to servers in the United States as necessary 
                      to provide you with the services that you requested, administer our contract with you or to respond to your 
                      requests as described in this Privacy Policy, and the data may be transmitted to our service providers supporting 
                      our business operations (described above). The United States may have data protection laws less stringent than or otherwise different 
                      from the laws in effect in the country in which you are located. Where we transfer your Personal Data out of the European Economic Area (EEA) 
                      we will take steps to ensure that your Personal Data receives an adequate level of protection 
                      where it is processed and your rights continue to be protected. `}
                  </Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`You may exercise your opt-out right by notifying us if you
                      wish to object to the use of your personal data for direct
                      marketing purposes. Please send requests for such
                      objections, access to data, correction of data,
                      information regarding policies and practices and kinds of
                      data held, questions or complaints to our Data Protection Officer via the contact details set out in Section 19 below.`}
                  </Text>
                </li>

                <li>
                  <Text type={Text.Type.PARAGRAPH}>{`Data Retention`}</Text>
                  <Text type={Text.Type.PARAGRAPH}>
                    {`Our policy is to keep your Personal Data only for as long as is reasonably necessary to fulfil the purposes for which it was collected and processed, including for the purposes of satisfying any legal, regulatory, accounting or reporting requirements. If you have elected to receive marketing communications from us, we retain information about your marketing preferences until you opt out of receiving these communications and in accordance with our policies.`}
                  </Text>

                  <Text type={Text.Type.PARAGRAPH}>
                    {`To determine the appropriate retention period for your Personal Data, we will consider the amount, nature, and sensitivity of the Personal Data, the potential risk of harm from unauthorized use or disclosure of your Personal Data, the purposes for which we use your Personal Data and whether we can achieve those purposes through other means, and the applicable legal requirements. In some circumstances we may anonymize your Personal Data so that it can no longer be associated with you, in which case it is no longer Personal Data.`}
                  </Text>
                </li>
              </ol>
            </li>
          </ol>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`12. Your Choices`}
          </Text>

          <Text type={Text.Type.PARAGRAPH}>
            {`You can use the Site without providing any Personal Data. If you choose not to provide any Personal Data, you may not be able to use certain services.`}
          </Text>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`13. Exclusions`}
          </Text>

          <Text type={Text.Type.PARAGRAPH}>
            {`This Privacy Policy does not apply to any Personal Data collected by Hire Digital other than Personal Data collected through the Site or services. This Privacy Policy shall not apply to any unsolicited information you provide to Hire Digital through this Site or through any other means (without prejudice to your rights under the applicable law). This includes, but is not limited to, information posted to any public areas of the Site, such as forums (collectively, “Public Areas”), any ideas for new products or modifications to existing products, and other unsolicited submissions (collectively, “Unsolicited Information”). All Unsolicited Information shall be deemed to be non-confidential and Hire Digital shall be free to reproduce, use, disclose, distribute and exploit such Unsolicited Information without limitation or attribution.`}
          </Text>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`14. Children`}
          </Text>

          <Text type={Text.Type.PARAGRAPH}>
            {`Hire Digital does not knowingly collect Personal Data from children under the age of 13. If you are under the age of 13, please do not submit any Personal Data through the Site. We encourage parents and legal guardians to monitor their children’s Internet usage and to help enforce our Privacy Policy by instructing their children never to provide Personal Data on this Site without their permission. If you have reason to believe that a child under the age of 13 has provided Personal Data to Hire Digital through this Site, please contact us, and we will endeavor to delete that information from our databases.`}
          </Text>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`15. Links to Other Web Sites`}
          </Text>

          <Text type={Text.Type.PARAGRAPH}>
            {`This Site may contain links to other websites not operated or controlled by Hire Digital (“Third Party Sites”). The information that you share with Third Party Sites will be governed by the specific privacy policies and terms of service of the Third Party Sites and not by this Privacy Policy. By providing these links we do not imply that we endorse or have reviewed the Third Party Sites. We suggest contacting those sites directly for information on their privacy practices and policies.`}
          </Text>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`16. Security`}
          </Text>

          <Text type={Text.Type.PARAGRAPH}>
            {`Hire Digital takes reasonable and appropriate steps to protect the Personal Data provided via the Site or the services from loss, misuse, and unauthorized access, disclosure, alteration, or destruction. However, no Internet, email or other electronic transmission is ever fully secure or error free, so you should take special care in deciding what information you send to us in this way.`}
          </Text>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`17. Other Terms and Conditions`}
          </Text>

          <Text type={Text.Type.PARAGRAPH}>
            {`Your access to and use of this Site is subject to our Website Terms & Conditions.`}
          </Text>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`18. Changes to Hire Digital’s Privacy Policy`}
          </Text>

          <Text type={Text.Type.PARAGRAPH}>
            {`The Site and our business may change from time to time. As a result, at times it may be necessary for Hire Digital to make changes to this Privacy Policy. Hire Digital reserves the right to update or modify this Privacy Policy at any time and from time to time without prior notice, unless otherwise required by the applicable law. Please review this policy periodically, and especially before you provide any Personal Data. Your continued use of the Site after any changes or revisions to this Privacy Policy shall indicate your agreement with the terms of such revised Privacy Policy, without prejudice to your rights under the applicable law.`}
          </Text>

          <Text element='h3' size={Text.Size.SUBHEADLINE}>
            {`19. Contacting Hire Digital`}
          </Text>

          <Text type={Text.Type.PARAGRAPH}>
            {`Please contact us if you have any questions about Hire Digital’s Privacy Policy or the information practices of this Site.`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>
            {`You may contact us as follows: by email at dpo@hiredigital.com, or by post at:`}
          </Text>

          <Text type={Text.Type.PARAGRAPH}>
            {`Hire Digital Corporation`}
            <br />
            {`Attention: Privacy Policy Query`}
            <br />
            {`244 Madison Ave Suite 1637`}
            <br />
            {`New York, NY 10016`}
          </Text>
          <Text type={Text.Type.PARAGRAPH}>{`Last updated: January 1, 2021`}</Text>
        </Section>
      </div>
    </Layout>
  );
};

export default PrivacyPage;
